import { useEffect, useLayoutEffect, useState } from 'react'
import { classifyViewportRuntime } from '../../utils/visualViewport'
import { getNativePlatform } from '../../utils/nativeRuntime'
import { readPreviewDeviceOverride } from '../../utils/previewDeviceOverride'
import {APP_SETTINGS} from '../../config/appSettings'
import { NativeIntentInbox } from '../../native/nativeIntentInbox'
import { readStudioPreference, studioAvailable, writeStudioPreference, type StudioPreference } from './preference'

function storage(): Storage | undefined { try { return window.localStorage } catch { return undefined } }
function readRuntime() {
  return classifyViewportRuntime({
    nativePlatform: getNativePlatform(window),
    previewDeviceOverride: readPreviewDeviceOverride(),
    standalone: window.matchMedia('(display-mode: standalone)').matches,
    userAgent: navigator.userAgent,
  })
}

export function useStudioDesign() {
  const [preference, setPreference] = useState(() => readStudioPreference(storage()))
  const [runtime, setRuntime] = useState(readRuntime)
  const desktopLayout = studioAvailable(runtime)
  useLayoutEffect(() => {
    // Consume central classification changes, including the development device preview.
    // No component-local viewport measurements or resize/matchMedia listeners.
    const observer = new MutationObserver(() => setRuntime(readRuntime()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-viewport-form-factor', 'data-viewport-display-mode'] })
    return () => observer.disconnect()
  }, [])
  useLayoutEffect(() => {
    const root = document.documentElement
    root.dataset.design = desktopLayout ? 'studio' : 'mobile'
    root.dataset.studioAppearance = preference.appearance
    root.dataset.studioPalette = preference.palette
    root.style.setProperty('--studio-motion-quick-duration',`${APP_SETTINGS.motion.quickMs}ms`)
    root.style.setProperty('--studio-motion-modal-duration',`${APP_SETTINGS.motion.modalMs}ms`)
    root.style.setProperty('--studio-motion-state-duration',`${APP_SETTINGS.motion.stateMs}ms`)
    root.style.setProperty('--studio-processing-cycle', `${APP_SETTINGS.motion.processing.cycleMs}ms`)
    root.style.setProperty('--studio-processing-bloom-step', `${APP_SETTINGS.motion.processing.bloomStepMs}ms`)
    root.style.setProperty('--studio-processing-diagonal-step', `${APP_SETTINGS.motion.processing.diagonalStepMs}ms`)
    root.style.setProperty('--studio-processing-cognitive-step', `${APP_SETTINGS.motion.processing.cognitiveStepMs}ms`)
    root.style.setProperty('--shimmer-dur', `${APP_SETTINGS.motion.linkPreview.shimmerMs}ms`)
    // Browser chrome consumes the resolved CSS palette; no second JS color table.
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (themeColor) themeColor.content = getComputedStyle(root).getPropertyValue('--studio-canvas').trim()
    return () => {
      for (const property of ['--studio-processing-cycle', '--studio-processing-bloom-step', '--studio-processing-diagonal-step', '--studio-processing-cognitive-step', '--shimmer-dur']) root.style.removeProperty(property)
      delete root.dataset.design;delete root.dataset.studioAppearance;delete root.dataset.studioPalette
      root.style.removeProperty('--studio-motion-quick-duration');root.style.removeProperty('--studio-motion-modal-duration');root.style.removeProperty('--studio-motion-state-duration')
    }
  }, [desktopLayout, preference.appearance, preference.palette])
  useEffect(() => {
    if (getNativePlatform(window) !== 'android') return
    // Native appearance mirroring is post-render and never gates the editor or native intent adoption.
    const appearanceUpdate = NativeIntentInbox.setStudioAppearance?.({ palette: preference.palette, appearance: preference.appearance })
    void appearanceUpdate?.catch(() => console.warn('[Studio] Native appearance could not be updated'))
  }, [preference.palette, preference.appearance])
  function change(next: StudioPreference) {
    const saved = { ...next, palette: 'brass' } as const
    const persisted = writeStudioPreference(saved, storage())
    setPreference(saved)
    return persisted
  }
  return { runtime, desktopLayout, preference, change }
}
